import * as AmplitudeBrowser from '@amplitude/analytics-browser';

import StorageUtils, { StorageKeyName } from 'chairisher/util/storage';

import {
    getAmplitudeApiKey,
    getAmplitudeDeviceIdFromLocalStorage,
    getAmplitudeDeviceIdFromServer,
    shouldAllowAmplitudeAnalytics,
} from 'chairisher/context/analytics';
import { getGuid, isIdentified } from 'chairisher/context/auth';

let isAmplitudeBrowserActive = false;

/**
 * Custom function to determine if the current amplitude session is new for the user.
 * Attempts to replicate the behavior of the legacy isNewSession() function.
 * Note: no current session id indicates the amplitude browser sdk is not yet ready
 *
 * @returns {boolean} true if the current amplitude session is new for the user; otherwise, false
 */
export const isNewAmplitudeSession = () => {
    if (!isAmplitudeBrowserActive) {
        console.error('no amplitude sdk available to determine isNewAmplitudeSession, defaulting to true');
        return true;
    }

    try {
        const lastSessionId = StorageUtils.getItem(StorageKeyName.AMPLITUDE_SESSION_ID);
        const currentSessionId = AmplitudeBrowser.getSessionId();

        if (!currentSessionId) {
            if (!lastSessionId) {
                return true;
            }

            return false;
        }

        if (!lastSessionId) {
            StorageUtils.setItem(StorageKeyName.AMPLITUDE_SESSION_ID, String(currentSessionId));
            return true;
        }

        const isNewUserSession = String(currentSessionId) !== lastSessionId;
        if (isNewUserSession) {
            StorageUtils.setItem(StorageKeyName.AMPLITUDE_SESSION_ID, String(currentSessionId));
        }

        return isNewUserSession;
    } catch (e) {
        console.error('failed to determine isNewAmplitudeSession on amplitude browser, defaulting to true:', e);
        return true;
    }
};

/**
 * Initializes the Amplitude Browser SDK 2.
 *
 * @returns {AmplitudeBrowser|null} the Amplitude Browser instance, if conditions are met; otherwise, null
 */
export default function initializeAmplitude() {
    if (shouldAllowAmplitudeAnalytics()) {
        const amplitudeApiKey = getAmplitudeApiKey();
        if (amplitudeApiKey) {
            try {
                let deviceId = getAmplitudeDeviceIdFromLocalStorage();

                if (!deviceId) {
                    deviceId = getAmplitudeDeviceIdFromServer();
                }

                AmplitudeBrowser.init(amplitudeApiKey, {
                    // @see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#autocapture
                    autocapture: {
                        attribution: true,
                        pageViews: false,
                        sessions: false,
                        formInteractions: false,
                        fileDownloads: false,
                        // all of the above event types are true by default (attribution added for clarity)
                        // elementInteractions, frustrationInteractions, and networkTracking are false by default
                    },
                    deviceId,
                    userId: isIdentified() ? getGuid() : undefined,
                });

                isAmplitudeBrowserActive = true;
                return AmplitudeBrowser;
            } catch (e) {
                console.error('failed to initialize amplitude browser:', e);
            }
        }
    }

    return null;
}
